import { IdPathParameters, SaveOperation } from '@infuse/api/common';
import { CreateRole, Role } from '@infuse/iam/role/type';
import { UiNotificationType } from '@infuse/ui/domain';
import { useNotify } from '@infuse/ui/services';
import { getAxiosErrorMessage } from '@infuse/utils';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useI18n } from 'vue-i18n';

export const useGenericCreateRole = ({
  apiFn,
  forcedParentId
}: {
  apiFn: SaveOperation<CreateRole, Role> | SaveOperation<CreateRole, IdPathParameters<string>>;
  forcedParentId?: string;
}) => {
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { t } = useI18n({ useScope: 'global' });

  const mutation = useMutation<unknown, unknown, { parentId?: string; role: CreateRole }>({
    mutationFn: async ({ parentId, role }) => {
      if (forcedParentId) {
        parentId = forcedParentId;
      }

      try {
        await apiFn(parentId ? { id: parentId } : {}, role);

        notify({
          title: t('iam.roles.header.create_role'),
          message: t('iam.roles.notify.create_role_success')
        });
      } catch (e) {
        notify({
          type: UiNotificationType.ERROR,
          title: t('iam.roles.header.create_role'),
          message: getAxiosErrorMessage(e, t('iam.roles.notify.create_role_failed'))
        });
        throw e;
      }
    },

    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['roles'] });
    }
  });

  return {
    ...mutation
  };
};
